/**
 * Group-based access control — resolution and matching utilities.
 *
 * This module provides:
 *   - resolveUserAccess(): loads a user's groups from Firestore, merges
 *     route patterns and capabilities into a single resolved access set.
 *   - hasRouteAccess(): checks whether a path matches any allowed route pattern.
 *   - hasCapability(): checks whether a capability is in the resolved set.
 */

import { collection, getDocs, query, where, documentId } from 'firebase/firestore';
import { matchPath } from 'react-router-dom';
import { db } from './firebase';
import { COLLECTION_NAMES } from '@monthly-data-collection/shared';
import type { Capability } from '@monthly-data-collection/shared';

// ── Resolved access (output of group resolution) ───────────────────────────

export interface ResolvedAccess {
  allowedRoutes: Set<string>;
  capabilities: Set<Capability>;
  groupIds: string[];
  isSuperAdmin: boolean;
}

const EMPTY_ACCESS: ResolvedAccess = {
  allowedRoutes: new Set(),
  capabilities: new Set(),
  groupIds: [],
  isSuperAdmin: false,
};

// ── Group resolution ────────────────────────────────────────────────────────

/**
 * Resolve a user's effective access from their group memberships.
 *
 * 1. If `isSuperAdmin` is true, returns a special marker that grants everything.
 * 2. Otherwise, batch-loads all referenced `user_groups` docs.
 * 3. Filters to `isActive === true` groups.
 * 4. Merges all routes and capabilities via union.
 */
export async function resolveUserAccess(
  groupIds: string[] | undefined,
  isSuperAdmin: boolean | undefined,
): Promise<ResolvedAccess> {
  if (isSuperAdmin) {
    return {
      allowedRoutes: new Set<string>(),
      capabilities: new Set<Capability>(),
      groupIds: groupIds ?? [],
      isSuperAdmin: true,
    };
  }

  if (!groupIds || groupIds.length === 0) {
    return EMPTY_ACCESS;
  }

  // Firestore `in` queries are limited to 30 values per clause.
  // Users will typically have 2-5 groups, but handle the edge case.
  const chunks: string[][] = [];
  for (let i = 0; i < groupIds.length; i += 30) {
    chunks.push(groupIds.slice(i, i + 30));
  }

  const allowedRoutes = new Set<string>();
  const capabilities = new Set<Capability>();

  for (const chunk of chunks) {
    const groupsRef = collection(db, COLLECTION_NAMES.USER_GROUPS);
    const q = query(groupsRef, where(documentId(), 'in', chunk));
    const snapshot = await getDocs(q);

    for (const doc of snapshot.docs) {
      const data = doc.data();

      // Skip inactive groups
      if (data.isActive === false) continue;

      if (Array.isArray(data.routes)) {
        for (const route of data.routes) {
          allowedRoutes.add(route);
        }
      }

      if (Array.isArray(data.capabilities)) {
        for (const cap of data.capabilities) {
          capabilities.add(cap as Capability);
        }
      }
    }
  }

  return {
    allowedRoutes,
    capabilities,
    groupIds,
    isSuperAdmin: false,
  };
}

// ── Access check utilities ──────────────────────────────────────────────────

/**
 * Check whether a path is allowed by the resolved route set.
 *
 * Super-admins bypass all checks. For regular users, the path is tested
 * against every pattern in `allowedRoutes` using React Router's `matchPath`.
 */
export function hasRouteAccess(path: string, access: ResolvedAccess): boolean {
  if (access.isSuperAdmin) return true;

  for (const pattern of access.allowedRoutes) {
    if (matchPath(pattern, path)) return true;
  }

  return false;
}

/**
 * Check whether a capability is in the resolved set.
 * Super-admins have all capabilities.
 */
export function hasCapability(cap: Capability, access: ResolvedAccess): boolean {
  if (access.isSuperAdmin) return true;
  return access.capabilities.has(cap);
}
